import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useCollection } from '../../../hooks/useCollection';
import { formatDateFR } from '../../shared/utils/helpers';
import MokhalafatForm from './MokhalafatForm';
import { printMokhalafaNarsa } from '../utils/printNarsa';
import { Plus, Edit2, Trash2 } from 'lucide-react';

export default function MokhalafatPage({ settings }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';

  const { data: mokhalafat, loading: loadingMokhalafat, remove: removeMokhalafa, update: updateMokhalafa } = useCollection('mokhalafat');
  const { data: rentals, loading: loadingRentals } = useCollection('rentals');
  const { data: clients, loading: loadingClients } = useCollection('clients');
  const { data: cars, loading: loadingCars } = useCollection('cars');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  const handleDelete = async (id) => {
    if (!confirm(isAr ? 'حذف هذه المخالفة؟' : 'Supprimer?')) return;
    try {
      await removeMokhalafa(id);
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const handleQuickStatus = async (m, newStatus) => {
    try {
      await updateMokhalafa(m.id, { status: newStatus });
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  const stats = useMemo(() => {
    const pending = mokhalafat.filter(m => m.status === 'pending').length;
    const transferred = mokhalafat.filter(m => m.status === 'transferred').length;
    const paid = mokhalafat.filter(m => m.status === 'paid').length;
    const totalAmount = mokhalafat.reduce((s, m) => s + (parseFloat(m.infractionAmount) || 0), 0);
    return { total: mokhalafat.length, pending, transferred, paid, totalAmount };
  }, [mokhalafat]);

  const filtered = useMemo(() => {
    let result = mokhalafat;
    if (filterStatus !== 'all') result = result.filter(m => m.status === filterStatus);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(m =>
        (m.clientName || '').toLowerCase().includes(q) ||
        (m.clientFirstName || '').toLowerCase().includes(q) ||
        (m.carPlate || '').toLowerCase().includes(q) ||
        (m.avisNumber || '').toLowerCase().includes(q)
      );
    }
    return [...result].sort((a, b) => (b.infractionDate || '').localeCompare(a.infractionDate || ''));
  }, [mokhalafat, filterStatus, search]);

  if (loadingMokhalafat || loadingRentals || loadingClients || loadingCars) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          ⚠️ {isAr ? `المخالفات (${mokhalafat.length})` : `Contraventions (${mokhalafat.length})`}
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          {/* NARSA Auto Check */}
          {settings.narsaAutoCheck && (
            <a 
              href="https://tasrih-saeq.narsa.gov.ma/InfractionPro/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white border-2 border-orange-400 hover:bg-orange-50 text-orange-700 px-4 py-2 rounded-lg font-bold text-sm transition shadow-sm"
              title={isAr ? 'تحقق من المخالفات في موقع NARSA' : 'Ouvrir tasrih-saeq.narsa.gov.ma'}
            >
              <span className="text-base">🌐</span>
              <span>{isAr ? 'تحقق NARSA' : 'Vérifier NARSA'}</span>
              <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-mono">↗</span>
            </a>
          )}
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            <Plus className="w-5 h-5" />
            {isAr ? 'مخالفة جديدة' : 'Nouvelle contravention'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="text-[10px] font-bold text-amber-700 uppercase">
            ⏳ {isAr ? 'في الانتظار' : 'En attente'}
          </div>
          <div className="text-2xl font-black text-amber-900">{stats.pending}</div>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="text-[10px] font-bold text-blue-700 uppercase">
            ✅ {isAr ? 'محولة NARSA' : 'Transférées'}
          </div>
          <div className="text-2xl font-black text-blue-900">{stats.transferred}</div>
        </div>
        <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
          <div className="text-[10px] font-bold text-green-700 uppercase">
            💰 {isAr ? 'مدفوعة' : 'Payées'}
          </div>
          <div className="text-2xl font-black text-green-900">{stats.paid}</div>
        </div>
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
          <div className="text-[10px] font-bold text-red-700 uppercase">
            💵 {isAr ? 'المجموع' : 'Total'}
          </div>
          <div className="text-base font-black text-red-900 font-mono">
            {stats.totalAmount.toFixed(0)} {settings.currency || 'MAD'}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap gap-2 items-center">
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded-lg bg-white"
        >
          <option value="all">{isAr ? 'الكل' : 'Toutes'}</option>
          <option value="pending">⏳ {isAr ? 'في الانتظار' : 'En attente'}</option>
          <option value="transferred">✅ {isAr ? 'محولة' : 'Transférées'}</option>
          <option value="paid">💰 {isAr ? 'مدفوعة' : 'Payées'}</option>
          <option value="cancelled">❌ {isAr ? 'ملغاة' : 'Annulées'}</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={isAr ? '🔍 بحث...' : '🔍 Rechercher...'}
          className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
        />
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-5xl mb-3">⚠️</div>
          <p className="text-gray-500 font-medium">
            {mokhalafat.length === 0
              ? (isAr ? 'لا توجد مخالفات' : 'Aucune contravention')
              : (isAr ? 'لا توجد نتائج' : 'Aucun résultat')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(m => {
            const statusColors = {
              pending: { bg: 'bg-amber-550 bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500', text: isAr ? '⏳ في الانتظار' : '⏳ En attente' },
              transferred: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-500', text: isAr ? '✅ محولة' : '✅ Transférée NARSA' },
              paid: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-500', text: isAr ? '💰 مدفوعة' : '💰 Payée' },
              cancelled: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-500', text: isAr ? '❌ ملغاة' : '❌ Annulée' }
            };
            // Clean simple typo from the original if exists
            const sc = statusColors[m.status] || statusColors.pending;

            return (
              <div
                key={m.id}
                className={`p-3 border rounded-xl shadow-sm ${sc.bg} ${sc.border}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xl">⚠️</span>
                      <span className="font-bold text-gray-900">
                        {m.clientName || ''} {m.clientFirstName || ''}
                      </span>
                      <span className={`px-2 py-0.5 ${sc.badge} text-white text-[10px] font-bold rounded-full`}>
                        {sc.text}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div className="font-mono">
                        🚗 {m.carName} ({m.carPlate})
                      </div>
                      {m.avisNumber && (
                        <div className="font-mono text-[10px]">
                          🔢 Avis: {m.avisNumber}{m.avisRF ? ` RF ${m.avisRF}` : ''}
                        </div>
                      )}
                      <div className="font-mono">
                        📅 {formatDateFR(m.infractionDate) || '-'}
                        {m.infractionType ? ` • ${m.infractionType}` : ''}
                      </div>
                      {m.infractionAmount > 0 && (
                        <div className="font-bold text-red-700">
                          💰 {m.infractionAmount} {settings.currency || 'MAD'}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => printMokhalafaNarsa(m, settings)}
                      className="px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold transition flex items-center gap-1"
                      title={isAr ? 'طباعة نموذج NARSA' : 'Imprimer NARSA'}
                    >
                      🖨️ NARSA
                    </button>
                    {m.status === 'pending' && (
                      <button
                        onClick={() => handleQuickStatus(m, 'transferred')}
                        className="p-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition"
                        title={isAr ? 'تمت إلى NARSA' : 'Marquer transférée'}
                      >
                        ✅
                      </button>
                    )}
                    {m.status !== 'paid' && (
                      <button
                        onClick={() => handleQuickStatus(m, 'paid')}
                        className="p-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg transition"
                        title={isAr ? 'دفع' : 'Marquer payée'}
                      >
                        💰
                      </button>
                    )}
                    <button
                      onClick={() => { setEditing(m); setShowForm(true); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <MokhalafatForm
          mokhalafa={editing}
          rentals={rentals}
          clients={clients}
          cars={cars}
          settings={settings}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
